import { MinusIcon, PlusIcon } from "@heroicons/react/24/outline";
import { DEFAULT_MAX_QUANTITY } from "@/constants/common";
import useAppDispatch from "@/hooks/useAppDispatch";
import clsx from "clsx";

interface QuantitySelectorProps {
  quantity: number;
  size?: "sm" | "lg";
  handleQuantityChange: (value: number) => void;
}

const QuantitySelector = ({
  quantity,
  size = "sm",
  handleQuantityChange,
}: QuantitySelectorProps) => {
  const appDispatch = useAppDispatch();

  const validateQuantity = (value: number) => {
    if (value > DEFAULT_MAX_QUANTITY) {
      appDispatch.pushAlert({
        type: "warning",
        title: `數量上限為 ${DEFAULT_MAX_QUANTITY} 件`,
      });
      return DEFAULT_MAX_QUANTITY;
    }
    return Math.max(value, 1); // 確保數量至少為 1
  };

  const handleQuantityStepChange = (step: 1 | -1) => {
    const nextQuantity = validateQuantity(quantity + step);
    if (nextQuantity !== quantity) {
      handleQuantityChange(nextQuantity);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    const parsedValue = parseInt(rawValue, 10);

    if (isNaN(parsedValue)) {
      appDispatch.pushAlert({ type: "warning", title: "請輸入有效的數字" });
      return;
    }

    const value = validateQuantity(parsedValue);
    handleQuantityChange(value);
  };

  return (
    <div className="flex items-center gap-4 bg-white">
      <div
        className={clsx("flex max-w-32 items-center justify-between rounded-sm border", {
          "p-0": size === "sm",
          "px-1 py-2": size === "lg",
        })}
      >
        <button
          aria-label="減少數量"
          onClick={() => handleQuantityStepChange(-1)}
          disabled={quantity <= 1}
          className={clsx({
            "p-1": size === "sm",
            "p-2": size === "lg",
          })}
        >
          <MinusIcon className="size-4 text-secondary" />
        </button>
        <input
          name="quantity"
          type="number"
          value={quantity ?? 0}
          onChange={handleInputChange}
          className="input input-sm w-full max-w-xs text-center"
          min={1}
          max={DEFAULT_MAX_QUANTITY}
        />
        <button
          aria-label="增加數量"
          onClick={() => handleQuantityStepChange(1)}
          disabled={quantity >= DEFAULT_MAX_QUANTITY}
          className={clsx({
            "p-1": size === "sm",
            "p-2": size === "lg",
          })}
        >
          <PlusIcon className="size-4 text-secondary" />
        </button>
      </div>
    </div>
  );
};

export default QuantitySelector;
